// Zustände einer Seite, die überall gleich aussehen: lädt (Skelett), nicht gefunden, Recht fehlt, abgestürzt.
// Die Hülle setzt sie um jede Modul-Route; Module nutzen <NotFound /> selbst, wenn ein Datensatz fehlt.
import { ArrowLeft } from 'lucide-react'
import { Component, use, useEffect, useRef, useState, type ErrorInfo, type ReactNode } from 'react'
import { isRouteErrorResponse, UNSAFE_NavigationContext, useRouteError } from 'react-router'
import type { PermissionKey } from '@shared/contract'
import { isAppError } from '@shared/errors'
import { fetchServerVersion, loadedEntryScript } from '../../lib/appVersion'
import { useConnectionStore, isOffline as connectionOffline } from '../../stores/connection'
import { EmptyState, ErrorState } from '../../ui/EmptyState'
import { PageHeader } from '../../ui/PageHeader'
import { Skeleton } from '../../ui/Skeleton'
import { requestIdOf } from '../client'
import { describeError } from '../errors'
import { permissionLabel } from '../permissions'
import { brand } from '@shared/brand'

const reload = (): void => window.location.reload()
const goBack = (): void => window.history.back()

/** Wechsel innerhalb der App, wenn ein Router da ist – sonst ein gewöhnlicher Seitenaufruf. */
function useGo(): (href: string) => void {
  const navigation = use(UNSAFE_NavigationContext) as { navigator?: { push: (to: string) => void } } | null
  return (href) => (navigation?.navigator ? navigation.navigator.push(href) : window.location.assign(href))
}

const frame = 'flex min-h-0 flex-1 flex-col'
// EmptyState (Größe md) bringt seinen Seitenrand selbst mit
const body = 'min-w-0'

/** Platzhalter, solange der Code einer Seite nachlädt: Titel steht schon, der Inhalt ist ein Skelett. */
export function PageSkeleton({ title, variant = 'table' }: { title?: string; variant?: 'table' | 'detail' | 'form' }) {
  return (
    <div className={frame} aria-busy="true" data-ui="page-skeleton">
      {title ? (
        <PageHeader title={title} breadcrumbsPlacement="none" />
      ) : (
        <div className="px-(--page-px) pt-5 pb-3">
          <Skeleton variant="block" width={220} height={28} />
        </div>
      )}
      <div className="px-(--page-px) pt-2 pb-6">
        {variant === 'table' ? (
          <Skeleton variant="table" rows={8} columns={[12, 34, 14, 14, 14, 12]} />
        ) : variant === 'detail' ? (
          <Skeleton variant="key-value" />
        ) : (
          <div className="flex max-w-180 flex-col gap-4">
            <Skeleton variant="block" height={40} />
            <Skeleton variant="block" height={40} />
            <Skeleton variant="block" height={96} />
          </div>
        )}
      </div>
    </div>
  )
}

export interface NotFoundProps {
  /** „Dieser Artikel wurde nicht gefunden“ */
  title?: string
  description?: string
  /** Standard: zur Startseite */
  back?: { label: string; href: string }
}

/** Seite oder Datensatz gibt es nicht (mehr). */
export function NotFound({
  title = 'Diese Seite gibt es nicht',
  description = 'Der Link ist veraltet oder der Datensatz wurde entfernt. Über die Suche finden Sie, was Sie brauchen.',
  back = { label: 'Zur Startseite', href: '/' },
}: NotFoundProps) {
  const go = useGo()
  return (
    <div className={frame} data-ui="not-found">
      <div className={body}>
        <EmptyState
          variant="no-results"
          title={title}
          description={description}
          action={{ id: 'back', label: back.label, onSelect: () => go(back.href) }}
          secondaryAction={{ id: 'history-back', label: 'Zurück', icon: ArrowLeft, onSelect: goBack }}
        />
      </div>
    </div>
  )
}

/** Das Recht für diese Seite fehlt – die Meldung nennt es beim Namen (design-system §6.5). */
export function Forbidden({ permission, feature }: { permission?: PermissionKey; feature?: boolean }) {
  return (
    <div className={frame} data-ui="forbidden">
      <div className={body}>
        <EmptyState
          variant="no-permission"
          title={
            feature
              ? 'Diese Funktion ist für Ihren Betrieb nicht aktiviert.'
              : permission && !permission.startsWith('@')
                ? `Für „${permissionLabel(permission)}“ fehlt Ihnen das Recht.`
                : 'Dafür fehlt Ihnen die Berechtigung.'
          }
          description={
            feature
              ? 'Funktionen schaltet die Verwaltung unter Einstellungen frei.'
              : 'Wenden Sie sich an die Person, die bei Ihnen die Rollen vergibt.'
          }
          secondaryAction={{ id: 'history-back', label: 'Zurück', icon: ArrowLeft, onSelect: goBack }}
        />
      </div>
    </div>
  )
}

export const isChunkError = (error: unknown): boolean =>
  error instanceof Error &&
  /dynamically imported module|Importing a module script failed|Failed to fetch dynamically|ChunkLoadError/i.test(
    error.message,
  )

/** Abstand der Neuversuche, solange der Server nicht antwortet */
export const CHUNK_RETRY_MS = 5000

type ChunkCause = 'checking' | 'offline' | 'updated' | 'failed'

/**
 * Der Code einer Seite ließ sich nicht nachladen. Das heißt nicht von selbst „neue Fassung“: Meist ist nur die
 * Verbindung weg (WLAN-Loch im Lager, Server neu gestartet). Darum erst den Server fragen:
 *   • keine Antwort → „Keine Verbindung“; neuer Versuch alle 5 s und sobald das Gerät wieder online ist – antwortet
 *     der Server, lädt die Anwendung neu (die Seite war nie da, es geht nichts verloren). Kein „Neu laden“ ins Leere:
 *     offline führte das auf die Fehlerseite des Browsers bzw. der App.
 *   • anderes Einstiegsskript → „Die Anwendung wurde aktualisiert“ mit „Neu laden“
 *   • gleiche Fassung → „Diese Seite konnte nicht geladen werden“ mit „Neu laden“
 */
function ChunkFailure() {
  const offlineNow = useConnectionStore(connectionOffline)
  const [cause, setCause] = useState<ChunkCause>(() => (offlineNow ? 'offline' : 'checking'))
  const causeRef = useRef(cause)
  const busy = useRef(false)
  const alive = useRef(true)
  const update = (next: ChunkCause): void => {
    causeRef.current = next
    setCause(next)
  }

  const probe = async (): Promise<void> => {
    if (busy.current) return
    busy.current = true
    try {
      const version = await fetchServerVersion()
      if (!alive.current) return
      if (!version.reachable) return update('offline')
      // war offline und der Server antwortet wieder: weiter, wo die Person war
      if (causeRef.current === 'offline') return reload()
      const current = loadedEntryScript()
      update(version.entry && current && version.entry !== current ? 'updated' : 'failed')
    } finally {
      busy.current = false
    }
  }

  useEffect(() => {
    alive.current = true
    void probe()
    return () => {
      alive.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- einmal beim Einhängen; weitere Versuche unten
  }, [])

  useEffect(() => {
    if (cause !== 'offline') return
    const timer = setInterval(() => void probe(), CHUNK_RETRY_MS)
    const again = (): void => void probe()
    window.addEventListener('online', again)
    document.addEventListener('visibilitychange', again)
    return () => {
      clearInterval(timer)
      window.removeEventListener('online', again)
      document.removeEventListener('visibilitychange', again)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- probe liest nur Refs und setState
  }, [cause])

  if (cause === 'checking') return <PageSkeleton />
  if (cause === 'offline') {
    return (
      <div className={frame} data-ui="chunk-offline">
        <div className={body}>
          <ErrorState
            title="Keine Verbindung zum Server"
            message={`Diese Seite ließ sich nicht laden, weil ${brand.name} gerade nicht erreichbar ist. Sobald die Verbindung wieder steht, geht es von selbst weiter.`}
            onRetry={() => void probe()}
            retryLabel="Erneut versuchen"
          />
        </div>
      </div>
    )
  }
  if (cause === 'updated') {
    return (
      <div className={frame} data-ui="chunk-updated">
        <div className={body}>
          <ErrorState
            title="Die Anwendung wurde aktualisiert"
            message="Diese Seite gehört zu einer älteren Fassung. Laden Sie die Anwendung neu, dann geht es weiter."
            onRetry={reload}
            retryLabel="Neu laden"
          />
        </div>
      </div>
    )
  }
  return (
    <div className={frame} data-ui="chunk-failed">
      <div className={body}>
        <ErrorState
          title="Diese Seite konnte nicht geladen werden"
          message="Ihre Daten sind davon nicht betroffen. Laden Sie die Anwendung neu, dann geht es weiter."
          onRetry={reload}
          retryLabel="Neu laden"
        />
      </div>
    </div>
  )
}

/** Darstellung eines Fehlers an Stelle der Seite. */
export function CrashState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (isAppError(error) && error.code === 'NOT_FOUND') return <NotFound title={error.message} />
  if (isAppError(error) && error.code === 'FORBIDDEN') {
    const permission = error.details?.permission
    return <Forbidden permission={typeof permission === 'string' ? permission : undefined} />
  }
  if (isAppError(error) && error.code === 'FEATURE_DISABLED') return <Forbidden feature />
  if (isChunkError(error)) return <ChunkFailure />
  const text = isAppError(error)
    ? describeError(error)
    : { title: 'Diese Seite konnte nicht angezeigt werden', description: undefined, reference: undefined }
  return (
    <div className={frame} data-ui="crash">
      <div className={body}>
        <ErrorState
          title={text.title}
          message={
            text.description ??
            'Ihre Daten sind davon nicht betroffen. Versuchen Sie es erneut oder laden Sie die Anwendung neu.'
          }
          onRetry={onRetry ?? reload}
          retryLabel={onRetry ? 'Erneut versuchen' : 'Neu laden'}
          reference={text.reference ?? requestIdOf(error)}
        />
      </div>
    </div>
  )
}

/** `errorElement` für Modul-Routen: Ein Fehler bleibt in der Arbeitsfläche, Navigation und Suche stehen weiter. */
export function RouteErrorBoundary() {
  const error = useRouteError()
  if (isRouteErrorResponse(error)) {
    return error.status === 404 ? <NotFound /> : <CrashState error={new Error(error.statusText)} />
  }
  if (import.meta.env.DEV && !isAppError(error)) console.error(error)
  return <CrashState error={error} />
}

interface SectionBoundaryProps {
  /** „Baustein“, „Reiter“ – steht in der Meldung */
  label?: string
  size?: 'sm' | 'md'
  children: ReactNode
}

/** Fehlergrenze für einen Teil der Seite (Baustein der Startseite, Reiter): Der Rest bleibt bedienbar. */
export class SectionErrorBoundary extends Component<SectionBoundaryProps, { error: unknown; attempt: number }> {
  override state = { error: null as unknown, attempt: 0 }

  static getDerivedStateFromError(error: unknown): { error: unknown } {
    return { error }
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    if (import.meta.env.DEV) console.error(error, info.componentStack)
  }

  override render(): ReactNode {
    if (this.state.error === null) return this.props.children
    const text = isAppError(this.state.error) ? describeError(this.state.error) : null
    return (
      <ErrorState
        size={this.props.size ?? 'sm'}
        title={text?.title ?? `${this.props.label ?? 'Der Bereich'} konnte nicht angezeigt werden`}
        message={text?.description}
        onRetry={() => this.setState((state) => ({ error: null, attempt: state.attempt + 1 }))}
        reference={text?.reference}
      />
    )
  }
}
